import client from '../client';
import type { ApiResponse } from '../../types/api';

export interface ABTest {
  _id: string;
  storeId: string;
  name: string;
  pageName: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  variants: ABVariant[];
  goalType?: string;
  goalEvent?: string;
  startDate?: string;
  endDate?: string;
  createdAt: string;
}

export interface ABVariant {
  id: string;
  name: string;
  versionId: string;
  weight: number;
  views?: number;
  conversions?: number;
  conversionRate?: number;
  revenue?: number;
}

export interface ABTestStats {
  totalViews: number;
  totalConversions: number;
  overallConversionRate: number;
  variants?: Record<string, { views: number; conversions: number; conversionRate: number; revenue: number }>;
}

export const abTestingApi = {
  list: async (storeId: string, params?: { status?: string }) => {
    const res = await client.post<ApiResponse<{ tests: ABTest[] }>>(
      `/dashboard/${storeId}/ab-tests/list`,
      { status: params?.status },
    );
    return res.data.data!;
  },

  get: async (storeId: string, testId: string) => {
    const res = await client.post<ApiResponse<{ test: ABTest; stats: ABTestStats }>>(
      `/dashboard/${storeId}/ab-tests/get`,
      { testId },
    );
    const d = res.data.data!;
    // Merge per-variant stats from stats.variants map onto variant objects
    if (d.stats?.variants && d.test?.variants) {
      d.test.variants = d.test.variants.map(v => {
        const vs = d.stats.variants?.[v.id];
        return vs ? { ...v, ...vs } : v;
      });
    }
    return d;
  },

  create: async (storeId: string, data: { name: string; pageName: string; variants: { name: string; versionId: string; weight: number }[]; goalType?: string; goalEvent?: string }) => {
    const res = await client.post<ApiResponse<{ testId: string; test: ABTest }>>(
      `/dashboard/${storeId}/ab-tests/create`,
      data,
    );
    const d = res.data.data!;
    // Backend returns test without _id; use testId from response
    if (d.test && !d.test._id) {
      d.test._id = d.testId;
    }
    return d;
  },

  updateStatus: async (storeId: string, testId: string, status: string) => {
    await client.post(`/dashboard/${storeId}/ab-tests/status`, { testId, status });
  },

  delete: async (storeId: string, testId: string) => {
    await client.post(`/dashboard/${storeId}/ab-tests/delete`, { testId });
  },
};
